import { type PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useSelectedTaskDependencyQueuesIds } from './components/GanttDependenciesGraph';
import { LocateProductionTask } from './hooks';
import type { GanttQueue } from './types';

type GanttRowIntersection = {
  queueId: number;
  ganttQueues: GanttQueue[];
};

/** ~3 rows above and below the visible window */
const ROW_OVERSCAN = '200px 0px';

/**
 * Render only queues which are in ViewPort, render also queues which has parents/children of selected
 * task to display dependencies
 */
export const GanttRowIntersection = ({ queueId, ganttQueues, children }: PropsWithChildren<GanttRowIntersection>) => {
  const queuesDependencies = useSelectedTaskDependencyQueuesIds(ganttQueues);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const target = containerRef.current;

    if (!target) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) {
          setIsVisible(entry.isIntersecting);
        }
      },
      {
        root: document.getElementById(LocateProductionTask.scrollContainerId),
        rootMargin: ROW_OVERSCAN,
        threshold: 0,
      },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Force render queues that have dependencies with selected task
  const shouldForceRender = queuesDependencies.includes(queueId);

  return (
    <div className="h-15.25 first-of-type:h-15" {...LocateProductionTask.addQueueSelector(queueId)} ref={containerRef}>
      {isVisible || shouldForceRender ? children : null}
    </div>
  );
};
